import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  Animated,
  ActivityIndicator,
  Alert,
  TextInput,
} from 'react-native';

import {
  AdminTable,
  AdminTableHeaderSearch,
  AdminTableInlineMessageRow,
  AdminTableNameEmailHeader,
  AdminTableNameEmailRow,
  adminTableMemberGridStyles,
  adminTableStyles,
} from '../components/adminTable';
import {
  FormFieldLabelWithInfo,
  FormInfoHelpModal,
  FormTwoOptionChoice,
} from '../components/forms';
import { DropdownModal } from '../components/forms/DropdownModal';
import { Header } from '../components/Header';
import {
  Sliders,
  Bell,
  Mail,
  ChevronDown,
  Clock,
  Users,
  Trash2,
} from '../components/icons';
import {
  ACCESSORY_VIEW_ID,
  KeyboardDismissToolbar,
} from '../components/shared/KeyboardDismissToolbar';
import {
  useKeyboardHeight,
  usePinBlockAboveKeyboard,
} from '../components/shared/keyboardInset';
import { SubHeader } from '../components/SubHeader';
import {
  DEFAULT_DIGEST_PREFERENCES,
  DIGEST_FREQUENCIES,
  DIGEST_FREQUENCIES_WITH_MINUTE,
  digestFrequencyLabel,
  holdsDigestOversightRole,
  parseDigestFrequency,
} from '../config/digestPreferenceKeys';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useRegisterPreferencesApprovalsLeaveGuard } from '../contexts/PreferencesApprovalsLeaveGuardContext';
import {
  useAddApprovalGroupMember,
  useApprovalGroup,
  useApprovalGroupCandidates,
  useRemoveApprovalGroupMember,
  useSetApprovalGroupEnabled,
} from '../hooks/queries/useApprovalGroup';
import { useConfirmationDialog } from '../hooks/useConfirmationDialog';
import { useRuntimeFeatureFlags } from '../hooks/useRuntimeFeatureFlags';
import { ApiError } from '../services/api';
import { roleService } from '../services/auth/roleService';
import { setDisplayTimeZone } from '../services/displayTimeZone';
import { networkService } from '../services/network';
import {
  PreferencesService,
  SupervisorRegion,
} from '../services/preferencesService';
import {
  fetchUserTimeZone,
  readDeviceTimeZone,
  saveUserTimeZonePreference,
} from '../services/userTimeZone';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { AppRole } from '../types/auth';
import {
  APPROVAL_GROUP_ABANDON_EMPTY_CANCEL,
  APPROVAL_GROUP_ABANDON_EMPTY_CONTINUE,
  APPROVAL_GROUP_ABANDON_EMPTY_MESSAGE,
  APPROVAL_GROUP_ABANDON_EMPTY_TITLE,
} from '../utils/approvalGroupAbandonMessage';
import {
  APPROVAL_GROUP_DISABLE_BLOCKED_TITLE,
  approvalGroupDisableBlockedMessage,
  isApprovalGroupDisableBlockedApiMessage,
} from '../utils/approvalGroupDisableMessage';
import { APPROVALS_PUBLIC_DOMAIN_NOTE } from '../utils/approvalGroupPublicDomainMessage';
import { TimeZoneFields } from './preferences/TimeZoneFields';
import {
  extractViewerDomain,
  isPublicViewerDomain,
} from '../utils/partnerViewerScope';

import type { DigestFrequency } from '../config/digestPreferenceKeys';

const isWeb = Platform.OS === 'web';
const CONTENT_PADDING = 20;
const APPROVALS_SEARCH_TOP_GAP = 12;

/**
 * The "Mention emails" choice (#1599 Story 2.4, FR-11): the option strings
 * and the stored boolean, one pair. `Immediately` is `true`, `With my digest`
 * is `false`; nothing else compares these strings.
 */
const MENTION_EMAIL_OPTIONS = ['Immediately', 'With my digest'] as const;
const mentionEmailOption = (immediately: boolean): string =>
  immediately ? MENTION_EMAIL_OPTIONS[0] : MENTION_EMAIL_OPTIONS[1];
// The row's name (owner quick-dev 2026-10-27, was "Mention emails"). The
// Mention Email's footer names it: re-copy `MENTION_FOOTER_NOTE` with it.
const MENTION_EMAIL_INFO_TITLE = 'Mention Notifications';
// #1599 Story 2.11 (AC 9): With my digest is ONE email, and a mention chosen
// for the digest is never dropped, so the bullets and the frequency hint say
// so. `MENTION_FOOTER_NOTE` (server `digest-render.ts`) is re-copied with them.
const MENTION_EMAIL_INFO_BULLETS = [
  'Immediately: an email within a minute of being @mentioned.',
  'With my digest: no separate mention email. Mentions come first in your one digest email, together with request changes and discussion messages.',
  'With my digest, mentions are in your digest even when Discussion Messages is off.',
] as const;

/**
 * The Frequency row's hint (#1599 Story 2.11, correction 11). It names "With
 * my digest", so it is shown only while the Mention emails row is (the
 * review's flag-off finding); otherwise the hint the row always had.
 */
const DIGEST_FREQUENCY_HINT =
  'How often your digest email may be sent. With my digest, request changes, messages and mentions come together.';
const DIGEST_FREQUENCY_HINT_WITHOUT_MENTIONS =
  'How often both emails may be sent.';

/**
 * The frequency tokens as the labels the picker shows, and the way back.
 *
 * Built from the keys module so the picker can never offer a value the route
 * would refuse — including the reverse: `allowMinute` is the SAME answer the
 * server gives its own `parseDigestFrequency`, so the extra option appears here
 * exactly when the write path would accept it.
 */
const digestFrequencyTokens = (
  allowMinute: boolean,
): readonly DigestFrequency[] =>
  // FIRST, not last: the picker's list scrolls at six items, so appended it sits
  // below the fold — and somebody who has just turned the flag on reads that as
  // "it did not work". It is also the one they came for.
  (allowMinute
    ? DIGEST_FREQUENCIES_WITH_MINUTE
    : DIGEST_FREQUENCIES) as readonly DigestFrequency[];

interface PreferencesProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

// Toggle Switch Component
interface ToggleSwitchProps {
  value: boolean;
  onValueChange: (value: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  testID?: string;
  /** On-track color; defaults to Preferences orange. Approvals uses success green. */
  onColor?: string;
}

// Reusable animated on/off switch. When no explicit `testID` is passed it
// derives one from the label (spaces/slashes → hyphens) so E2E selectors stay
// stable; pass `testID` explicitly for toggles asserted in automated tests.
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  value,
  onValueChange,
  label,
  description,
  disabled = false,
  testID,
  onColor = '#FF6B35',
}) => {
  const animatedValue = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: value ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [value, animatedValue]);

  const thumbTranslateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 20],
  });

  const trackColor = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['#ccc', onColor],
  });

  return (
    <View style={styles.toggleContainer}>
      <View style={styles.toggleLabelContainer}>
        <Text style={styles.toggleLabel}>{label}</Text>
        {description && (
          <Text style={styles.toggleDescription}>{description}</Text>
        )}
      </View>
      <TouchableOpacity
        testID={
          testID ?? `toggle-${label.toLowerCase().replace(/[\s\/]+/g, '-')}`
        }
        accessibilityRole="switch"
        // `disabled` belongs in the a11y state, not only on the Touchable: a
        // screen reader must announce a suppressed toggle as unavailable, and
        // the #1399 cascade disables every dependent toggle whenever the global
        // email switch is off (same contract as the frequency picker below).
        accessibilityState={{ checked: value, disabled }}
        style={[styles.toggleTrack, disabled && styles.toggleDisabled]}
        onPress={() => !disabled && onValueChange(!value)}
        disabled={disabled}
        activeOpacity={0.7}
      >
        <Animated.View
          style={[
            styles.toggleTrackInner,
            {
              backgroundColor: trackColor,
            },
          ]}
        >
          <Animated.View
            style={[
              styles.toggleThumb,
              {
                transform: [{ translateX: thumbTranslateX }],
              },
            ]}
          />
        </Animated.View>
      </TouchableOpacity>
    </View>
  );
};

/**
 * Application Preferences screen.
 *
 * Email notification toggles come in two tiers with DIFFERENT default models:
 *  - Global "Email notifications" (#1399): shown to every user, opt-OUT /
 *    default-ON. Absent preference means ON, only an explicit OFF suppresses.
 *    This is the master kill-switch; the server enforces it at send time
 *    (see server/services/notification-preference.service.ts).
 *  - Supervisor region toggles: shown only to users holding the matching
 *    SUPERVISOR_* role, opt-IN / default-OFF (a supervisor must turn them on).
 *    Turning the global switch OFF also clears any held region (persisted off);
 *    turning the global switch back ON never restores them — the supervisor must
 *    re-enable each region deliberately.
 *
 * All toggles save optimistically (flip immediately, revert on error) so the
 * UI never blocks or flashes a spinner mid-save.
 */
export const Preferences: React.FC<PreferencesProps> = ({
  navigate,
  currentPath,
}) => {
  const [retainAudioOcrFiles, setRetainAudioOcrFiles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeZone, setTimeZone] = useState('');
  const [syncWithDevice, setSyncWithDevice] = useState(false);
  const [zoneLoaded, setZoneLoaded] = useState(false);
  const [zoneLoadFailed, setZoneLoadFailed] = useState(false);
  const timeZoneSaveSeq = useRef(0);

  // Global email notifications switch (#1399). Opt-out model: default ON.
  const [emailNotifications, setEmailNotifications] = useState(true);

  // Supervisor email notification preferences (default to false - disabled, opt-in model)
  const [supervisorEmailNAM, setSupervisorEmailNAM] = useState(false);
  const [supervisorEmailEMEA, setSupervisorEmailEMEA] = useState(false);
  const [supervisorEmailAPAC, setSupervisorEmailAPAC] = useState(false);

  // Digest emails (#1507): two sources default ON, hourly, oversight default OFF.
  const [digestRequestChanges, setDigestRequestChanges] = useState<boolean>(
    DEFAULT_DIGEST_PREFERENCES.requestChangesEnabled,
  );
  const [digestCorrespondence, setDigestCorrespondence] = useState<boolean>(
    DEFAULT_DIGEST_PREFERENCES.correspondenceEnabled,
  );
  const [digestFrequency, setDigestFrequency] = useState<DigestFrequency>(
    DEFAULT_DIGEST_PREFERENCES.frequency,
  );
  const [digestIncludeOversight, setDigestIncludeOversight] = useState<boolean>(
    DEFAULT_DIGEST_PREFERENCES.includeOversight,
  );
  const [frequencyPickerOpen, setFrequencyPickerOpen] = useState(false);
  // Mention emails (#1599 Story 2.4): Immediately (`true`) by default.
  const [mentionEmailImmediately, setMentionEmailImmediately] =
    useState<boolean>(DEFAULT_DIGEST_PREFERENCES.mentionEmailEnabled);
  const [mentionEmailInfoOpen, setMentionEmailInfoOpen] = useState(false);

  // False until the server answers, so the development-only cadence is never
  // briefly offered to somebody who cannot have it (`DEFAULT_RUNTIME_FEATURE_FLAGS`).
  // `correspondenceMentions` is also false until the server answers: the
  // "Mention emails" row is a surface of a feature the deployment may not have.
  const {
    digestMinuteFrequency: allowMinuteFrequency,
    correspondenceMentions: showMentionEmailChoice,
  } = useRuntimeFeatureFlags();
  const digestFrequencyOptions = useMemo(
    () => digestFrequencyTokens(allowMinuteFrequency).map(digestFrequencyLabel),
    [allowMinuteFrequency],
  );
  const digestFrequencyByLabel = useMemo(
    () =>
      new Map<string, DigestFrequency>(
        digestFrequencyTokens(allowMinuteFrequency).map(token => [
          digestFrequencyLabel(token),
          token,
        ]),
      ),
    [allowMinuteFrequency],
  );

  // Get user roles from auth context
  const { user } = useAuth();
  const { isImpersonating } = useImpersonation();
  const isExternalUser = roleService.isExternalUser(user);
  const viewerDomain = extractViewerDomain(user?.email);
  const isPublicDomainApprovals =
    isExternalUser &&
    Boolean(viewerDomain) &&
    isPublicViewerDomain(viewerDomain!);
  /** External private-domain users can configure Approvals Group. */
  const canConfigureApprovals =
    isExternalUser &&
    Boolean(viewerDomain) &&
    !isPublicViewerDomain(viewerDomain!);
  /** Show Approvals section for any external user (public domains get a note only). */
  const showApprovalsCard = isExternalUser && Boolean(viewerDomain);
  const {
    data: approvalGroup,
    isLoading: approvalLoading,
    isError: approvalError,
    refetch: refetchApprovalGroup,
  } = useApprovalGroup();
  const [candidateSearch, setCandidateSearch] = useState('');
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [approvalsSearchFocused, setApprovalsSearchFocused] = useState(false);
  const approvalCandidateSearchRef = useRef<TextInput>(null);
  const scrollRef = useRef<ScrollView>(null);
  const scrollContentRef = useRef<View>(null);
  const approvalsCandidateSearchBlockRef = useRef<View>(null);
  const keyboardHeight = useKeyboardHeight();
  usePinBlockAboveKeyboard({
    active: approvalsSearchFocused,
    keyboardHeight,
    scrollRef,
    contentRef: scrollContentRef,
    blockRef: approvalsCandidateSearchBlockRef,
    contentPadding: CONTENT_PADDING,
    topGap: APPROVALS_SEARCH_TOP_GAP,
  });
  const [trashPressedUserId, setTrashPressedUserId] = useState<number | null>(
    null,
  );
  const [isAddingMembers, setIsAddingMembers] = useState(false);
  const addMembersInFlightRef = useRef(false);
  /** Latest selection for Add — do not re-filter against a live candidates list. */
  const selectedCandidateIdsRef = useRef(selectedCandidateIds);
  selectedCandidateIdsRef.current = selectedCandidateIds;
  const {
    data: candidatesData,
    isFetching: candidatesLoading,
    isError: candidatesError,
  } = useApprovalGroupCandidates(candidateSearch);
  const addMemberMutation = useAddApprovalGroupMember();
  const removeMemberMutation = useRemoveApprovalGroupMember();
  const setEnabledMutation = useSetApprovalGroupEnabled();
  const memberCount = approvalGroup?.members?.length ?? 0;
  const membersEditable = !approvalError && !approvalLoading;
  const approvalsWorkflowOn = Boolean(approvalGroup?.enabled);
  const approvalsGateOn = approvalsWorkflowOn && memberCount > 0;
  const approvalsSetupIncomplete = approvalsWorkflowOn && memberCount === 0;
  const approvalsMutationBusy =
    setEnabledMutation.isPending ||
    removeMemberMutation.isPending ||
    addMemberMutation.isPending;

  // The oversight switch is rendered only for roles that can read requests
  // without a team row (same list as the server's oversight readers).
  const isOversightRole = holdsDigestOversightRole(user?.roles);

  // Cross-platform confirmation modal (web + native) for the destructive
  // "turn off global notifications" action.
  const { confirm, alert, confirmationDialog } = useConfirmationDialog();

  const awaitingApprovalCount = approvalGroup?.awaitingApprovalCount ?? 0;

  const approvalLeaveStateRef = useRef({
    enabled: false,
    memberCount: 0,
  });
  useEffect(() => {
    approvalLeaveStateRef.current = {
      enabled: approvalsWorkflowOn,
      memberCount,
    };
  }, [approvalsWorkflowOn, memberCount]);

  const tryLeaveIncompleteApprovalsSetup =
    useCallback(async (): Promise<boolean> => {
      if (!canConfigureApprovals) {
        return true;
      }
      if (addMembersInFlightRef.current) {
        return false;
      }
      const { enabled, memberCount: count } = approvalLeaveStateRef.current;
      if (!enabled || count > 0) {
        return true;
      }
      const confirmed = await confirm({
        title: APPROVAL_GROUP_ABANDON_EMPTY_TITLE,
        message: APPROVAL_GROUP_ABANDON_EMPTY_MESSAGE,
        confirmText: APPROVAL_GROUP_ABANDON_EMPTY_CONTINUE,
        cancelText: APPROVAL_GROUP_ABANDON_EMPTY_CANCEL,
        confirmButtonStyle: 'danger',
        showWarningIcon: true,
        cancelButtonStyle: 'clear',
      });
      if (!confirmed) {
        return false;
      }
      try {
        await setEnabledMutation.mutateAsync(false);
      } catch {
        return false;
      }
      return true;
    }, [canConfigureApprovals, confirm, setEnabledMutation]);

  useRegisterPreferencesApprovalsLeaveGuard(
    canConfigureApprovals ? tryLeaveIncompleteApprovalsSetup : null,
  );

  const existingMemberIds = useMemo(() => {
    const ids = new Set<number>();
    for (const member of approvalGroup?.members ?? []) {
      const userId = Number(member.userId);
      if (Number.isInteger(userId) && userId > 0) {
        ids.add(userId);
      }
    }
    return ids;
  }, [approvalGroup?.members]);
  const existingMemberIdsRef = useRef(existingMemberIds);
  existingMemberIdsRef.current = existingMemberIds;
  const visibleCandidates = useMemo(
    () =>
      (candidatesData?.candidates ?? []).filter(candidate => {
        const userId = Number(candidate.userId);
        return (
          Number.isInteger(userId) &&
          userId > 0 &&
          !existingMemberIds.has(userId)
        );
      }),
    [candidatesData?.candidates, existingMemberIds],
  );
  const isCandidateSearchActive = candidateSearch.trim().length > 0;
  const approvalsAddBusy = approvalsMutationBusy || isAddingMembers;
  const approvalsToggleEnabled = membersEditable && !approvalsAddBusy;

  const clearApprovalPicker = () => {
    setCandidateSearch('');
    setSelectedCandidateIds(new Set());
  };

  const handleCandidateSearchChange = (value: string) => {
    setCandidateSearch(value);
    setSelectedCandidateIds(new Set());
  };

  const handleCandidateSelection = (userId: number) => {
    const id = Number(userId);
    if (!Number.isInteger(id) || id <= 0) {
      return;
    }
    setSelectedCandidateIds(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddApprovalMembers = async () => {
    const selectedIds = selectedCandidateIdsRef.current;
    const memberIds = existingMemberIdsRef.current;
    if (
      selectedIds.size === 0 ||
      addMembersInFlightRef.current ||
      approvalsAddBusy
    ) {
      return;
    }

    addMembersInFlightRef.current = true;
    setIsAddingMembers(true);

    const failedIds = new Set<number>();
    try {
      // Add from the selection snapshot — do not require the id to still appear
      // in the live candidates query (refetch/filter races were clearing the
      // checklist with no POST after the development merge).
      //
      // Use Array.from — webpack babel `loose` turns `[...set]` into
      // `[].concat(set)`, which does NOT expand a Set and would POST
      // `{ userId: {} }` → 400 "userId must be a positive integer".
      const idsToAdd = Array.from(selectedIds).filter(userId => {
        const id = Number(userId);
        return Number.isInteger(id) && id > 0 && !memberIds.has(id);
      });
      if (idsToAdd.length === 0) {
        clearApprovalPicker();
        return;
      }

      for (const userId of idsToAdd) {
        try {
          await addMemberMutation.mutateAsync(userId);
        } catch {
          failedIds.add(userId);
        }
      }

      if (failedIds.size > 0) {
        setSelectedCandidateIds(failedIds);
        await alert({
          title:
            failedIds.size === 1
              ? 'Unable to add member'
              : 'Unable to add members',
          message:
            failedIds.size === 1
              ? 'One selected user could not be added. Please try again.'
              : `${failedIds.size} selected users could not be added. Please try again.`,
        });
        return;
      }

      clearApprovalPicker();
    } finally {
      addMembersInFlightRef.current = false;
      setIsAddingMembers(false);
    }
  };

  const handleRemoveApprovalMember = async (userId: number) => {
    if (approvalsAddBusy) {
      return;
    }
    const isLastMember = memberCount === 1;
    if (approvalsGateOn && isLastMember) {
      if (awaitingApprovalCount > 0) {
        await alert({
          title: 'Unable to remove member',
          message: approvalGroupDisableBlockedMessage(awaitingApprovalCount),
        });
        return;
      }
      const confirmed = await confirm({
        title: 'Remove last group member?',
        message:
          'Removing the last member will turn off the Approvals Group. New submissions will no longer wait for group approval.',
        confirmText: 'Remove',
        cancelText: 'Cancel',
        confirmButtonStyle: 'danger',
      });
      if (!confirmed) {
        return;
      }
    }

    try {
      await removeMemberMutation.mutateAsync(userId);
      if (isLastMember) {
        clearApprovalPicker();
      }
    } catch {
      Alert.alert('Unable to remove member', 'Please try again.');
    }
  };

  const handleApprovalsEnabledToggle = async (value: boolean) => {
    if (approvalsAddBusy) {
      return;
    }
    if (value) {
      try {
        await setEnabledMutation.mutateAsync(true);
      } catch {
        Alert.alert('Unable to enable Approvals', 'Please try again.');
      }
      return;
    }

    addMembersInFlightRef.current = true;
    setIsAddingMembers(true);
    try {
      const { data: freshGroup } = await refetchApprovalGroup();
      const queueCount =
        freshGroup?.awaitingApprovalCount ??
        approvalGroup?.awaitingApprovalCount ??
        0;
      if (queueCount > 0) {
        await alert({
          title: APPROVAL_GROUP_DISABLE_BLOCKED_TITLE,
          message: approvalGroupDisableBlockedMessage(queueCount),
        });
        return;
      }

      const confirmed = await confirm({
        title: 'Turn off Approvals Group?',
        message:
          'New submissions from your organization will no longer wait for group approval before Vertiv can receive them.',
        confirmText: 'Turn Off',
        cancelText: 'Cancel',
        confirmButtonStyle: 'danger',
      });
      if (!confirmed) {
        return;
      }

      try {
        await setEnabledMutation.mutateAsync(false);
        clearApprovalPicker();
      } catch (error) {
        const message =
          error instanceof ApiError && error.message
            ? error.message
            : 'Please try again.';
        const title =
          error instanceof ApiError &&
          isApprovalGroupDisableBlockedApiMessage(message)
            ? APPROVAL_GROUP_DISABLE_BLOCKED_TITLE
            : 'Unable to turn off Approvals Group';
        await alert({
          title,
          message,
        });
      }
    } finally {
      addMembersInFlightRef.current = false;
      setIsAddingMembers(false);
    }
  };

  // Determine which supervisor roles the user has
  const isSupervisorNAM =
    user?.roles?.includes(AppRole.SUPERVISOR_NAM) ?? false;
  const isSupervisorEMEA =
    user?.roles?.includes(AppRole.SUPERVISOR_EMEA) ?? false;
  const isSupervisorAPAC =
    user?.roles?.includes(AppRole.SUPERVISOR_APAC) ?? false;

  // Load preferences on mount and whenever supervisor roles change. Supervisor
  // region values are only fetched for roles the user actually holds, so a
  // non-supervisor never issues those requests.
  useEffect(() => {
    const loadPreferences = async () => {
      setLoading(true);

      // Load each preference independently so a transient failure on one (e.g.
      // offline / 500) never prevents the others from loading. Otherwise a
      // failure on an earlier request could leave the global email toggle stuck
      // at its default ON, masking a user's saved OFF value.
      const loaders: Array<Promise<void>> = [
        (async () => {
          const value = await PreferencesService.getRetainAudioOcrFiles();
          setRetainAudioOcrFiles(value);
        })(),
        // Global email notifications preference (all users; default ON)
        (async () => {
          const emailNotificationsValue =
            await PreferencesService.getEmailNotificationsEnabled();
          setEmailNotifications(emailNotificationsValue);
        })(),
        // Digest emails (#1507) — one loader per key, so a failed read of one
        // leaves the others (and their defaults) intact.
        (async () => {
          setDigestRequestChanges(
            await PreferencesService.getDigestRequestChangesEnabled(),
          );
        })(),
        (async () => {
          setDigestCorrespondence(
            await PreferencesService.getDigestCorrespondenceEnabled(),
          );
        })(),
        (async () => {
          setDigestFrequency(await PreferencesService.getDigestFrequency());
        })(),
        // Mention emails (#1599 Story 2.4). Loaded whatever the mentions flag
        // says (the GET never 404s); only the row's render is gated.
        (async () => {
          setMentionEmailImmediately(
            await PreferencesService.getMentionEmailEnabled(),
          );
        })(),
      ];

      if (isOversightRole) {
        loaders.push(
          (async () => {
            setDigestIncludeOversight(
              await PreferencesService.getDigestIncludeOversight(),
            );
          })(),
        );
      }

      // Supervisor email preferences — only for roles the user actually holds.
      if (isSupervisorNAM) {
        loaders.push(
          (async () => {
            setSupervisorEmailNAM(
              await PreferencesService.getSupervisorEmailNotification('NAM'),
            );
          })(),
        );
      }
      if (isSupervisorEMEA) {
        loaders.push(
          (async () => {
            setSupervisorEmailEMEA(
              await PreferencesService.getSupervisorEmailNotification('EMEA'),
            );
          })(),
        );
      }
      if (isSupervisorAPAC) {
        loaders.push(
          (async () => {
            setSupervisorEmailAPAC(
              await PreferencesService.getSupervisorEmailNotification('APAC'),
            );
          })(),
        );
      }

      const results = await Promise.allSettled(loaders);

      if (results.some(r => r.status === 'rejected')) {
        results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .forEach(r => console.error('Error loading preference:', r.reason));
        Alert.alert('Error', 'Failed to load preference. Please try again.', [
          { text: 'OK' },
        ]);
      }

      setLoading(false);
    };

    loadPreferences();
  }, [isSupervisorNAM, isSupervisorEMEA, isSupervisorAPAC, isOversightRole]);

  useEffect(() => {
    let cancelled = false;
    void fetchUserTimeZone()
      .then(state => {
        if (cancelled) {
          return;
        }
        setTimeZone(
          state.timeZone || (isImpersonating ? '' : readDeviceTimeZone()),
        );
        setSyncWithDevice(!state.isLocked);
        setZoneLoadFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setZoneLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setZoneLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isImpersonating]);

  const saveTimeZoneChoice = async (
    nextZone: string,
    nextSync: boolean,
    previousZone: string,
    previousSync: boolean,
  ) => {
    if (zoneLoadFailed) {
      return;
    }
    const seq = ++timeZoneSaveSeq.current;
    if (!networkService.isConnected()) {
      setTimeZone(previousZone);
      setSyncWithDevice(previousSync);
      Alert.alert('Error', 'Time zone changes require a network connection.');
      return;
    }
    try {
      const saved = await saveUserTimeZonePreference(nextZone, !nextSync);
      if (seq !== timeZoneSaveSeq.current) {
        return;
      }
      const applied = saved.timeZone || nextZone;
      setDisplayTimeZone(applied);
      setTimeZone(applied);
      setSyncWithDevice(!saved.isLocked);
    } catch (error) {
      if (seq !== timeZoneSaveSeq.current) {
        return;
      }
      setTimeZone(previousZone);
      setSyncWithDevice(previousSync);
      const message =
        error instanceof Error ? error.message : 'Failed to save time zone';
      Alert.alert('Error', message);
    }
  };

  const handleTimeZoneChange = (nextZone: string) => {
    const previousZone = timeZone;
    const previousSync = syncWithDevice;
    setTimeZone(nextZone);
    void saveTimeZoneChoice(nextZone, previousSync, previousZone, previousSync);
  };

  const handleSyncChange = (nextSync: boolean) => {
    const previousZone = timeZone;
    const previousSync = syncWithDevice;
    if (nextSync && isImpersonating) {
      Alert.alert(
        'Error',
        'Device time zone is not applied while impersonating.',
      );
      return;
    }
    const deviceZone = readDeviceTimeZone();
    if (nextSync && !deviceZone) {
      Alert.alert('Error', 'Device time zone unavailable.');
      return;
    }
    const nextZone = nextSync ? deviceZone : previousZone;
    setSyncWithDevice(nextSync);
    setTimeZone(nextZone);
    void saveTimeZoneChoice(nextZone, nextSync, previousZone, previousSync);
  };

  const handleToggleChange = async (value: boolean) => {
    const previousValue = retainAudioOcrFiles;
    try {
      // Optimistic update — flip immediately, revert on error (no blocking spinner).
      setRetainAudioOcrFiles(value);
      await PreferencesService.setRetainAudioOcrFiles(value);
    } catch (error) {
      console.error('Error saving preference:', error);
      Alert.alert('Error', 'Failed to save preference. Please try again.', [
        { text: 'OK' },
      ]);
      // Revert to previous value on error
      setRetainAudioOcrFiles(previousValue);
    }
  };

  // Handler for the global email notifications toggle (#1399).
  // Optimistic update with revert-on-error (graceful-fail per D3).
  //
  // Off-only, no auto-restore cascade: turning the global switch OFF also clears
  // (persists off) every supervisor region the user holds, so re-enabling the
  // global switch never brings a supervisor toggle back on — the user must
  // manually re-enable each region.
  const handleEmailNotificationsToggle = async (value: boolean) => {
    if (!value) {
      // Turning OFF is the destructive direction. Confirm first, and — because it
      // also clears the per-region supervisor toggles below — warn supervisors.
      const isSupervisor =
        isSupervisorNAM || isSupervisorEMEA || isSupervisorAPAC;
      const confirmed = await confirm({
        title: 'Turn off email notifications?',
        message: isSupervisor
          ? 'This stops Site Scope request and team notification emails, including your regional supervisor emails.'
          : 'This stops Site Scope request and team notification emails.',
        confirmText: 'Turn Off',
        cancelText: 'Cancel',
        confirmButtonStyle: 'danger',
      });
      if (!confirmed) {
        // Leave the toggle in its current ON state; nothing to revert.
        return;
      }

      // Snapshot for revert, then optimistically flip global + held regions off.
      const previous = {
        global: emailNotifications,
        NAM: supervisorEmailNAM,
        EMEA: supervisorEmailEMEA,
        APAC: supervisorEmailAPAC,
      };
      setEmailNotifications(false);
      if (isSupervisorNAM) setSupervisorEmailNAM(false);
      if (isSupervisorEMEA) setSupervisorEmailEMEA(false);
      if (isSupervisorAPAC) setSupervisorEmailAPAC(false);

      try {
        const writes: Array<Promise<unknown>> = [
          PreferencesService.setEmailNotificationsEnabled(false),
        ];
        // Only persist regions the user holds that were actually on.
        if (isSupervisorNAM && previous.NAM) {
          writes.push(
            PreferencesService.setSupervisorEmailNotification('NAM', false),
          );
        }
        if (isSupervisorEMEA && previous.EMEA) {
          writes.push(
            PreferencesService.setSupervisorEmailNotification('EMEA', false),
          );
        }
        if (isSupervisorAPAC && previous.APAC) {
          writes.push(
            PreferencesService.setSupervisorEmailNotification('APAC', false),
          );
        }
        await Promise.all(writes);
      } catch (error) {
        console.error('Error saving email notifications preference:', error);
        Alert.alert('Error', 'Failed to save preference. Please try again.', [
          { text: 'OK' },
        ]);
        // Revert global + regions to their prior saved values on error.
        setEmailNotifications(previous.global);
        setSupervisorEmailNAM(previous.NAM);
        setSupervisorEmailEMEA(previous.EMEA);
        setSupervisorEmailAPAC(previous.APAC);
      }
      return;
    }

    // Turning ON only re-enables the global switch; supervisor regions stay at
    // their saved (off) values so the user re-enables them deliberately.
    const previousValue = emailNotifications;
    try {
      setEmailNotifications(true);
      await PreferencesService.setEmailNotificationsEnabled(true);
    } catch (error) {
      console.error('Error saving email notifications preference:', error);
      Alert.alert('Error', 'Failed to save preference. Please try again.', [
        { text: 'OK' },
      ]);
      // Revert to previous value on error
      setEmailNotifications(previousValue);
    }
  };

  // Handler for supervisor email notification toggles
  const handleSupervisorToggleChange = async (
    region: SupervisorRegion,
    value: boolean,
  ) => {
    // Store previous values for revert on error
    const previousValues = {
      NAM: supervisorEmailNAM,
      EMEA: supervisorEmailEMEA,
      APAC: supervisorEmailAPAC,
    };

    // Update state optimistically
    const setters = {
      NAM: setSupervisorEmailNAM,
      EMEA: setSupervisorEmailEMEA,
      APAC: setSupervisorEmailAPAC,
    };

    try {
      setters[region](value);
      await PreferencesService.setSupervisorEmailNotification(region, value);
    } catch (error) {
      console.error(
        `Error saving ${region} supervisor email preference:`,
        error,
      );
      Alert.alert('Error', 'Failed to save preference. Please try again.', [
        { text: 'OK' },
      ]);
      // Revert to previous value on error
      setters[region](previousValues[region]);
    }
  };

  // Digest switches (#1507): optimistic flip, revert on failure, no spinner.
  // Nothing cascades here — the server resets that source's bookmarks on an
  // off→on save, so the page has nothing to do beyond persisting the value.
  const handleDigestToggle = async (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    previousValue: boolean,
    save: (value: boolean) => Promise<void>,
    value: boolean,
  ) => {
    try {
      setter(value);
      await save(value);
    } catch (error) {
      console.error('Error saving digest preference:', error);
      Alert.alert('Error', 'Failed to save preference. Please try again.', [
        { text: 'OK' },
      ]);
      setter(previousValue);
    }
  };

  // Mention emails (#1599 Story 2.4): the option string maps to the boolean
  // here and nowhere else. A press on the selected option does nothing.
  const handleMentionEmailSelect = async (option: string) => {
    const next = option === MENTION_EMAIL_OPTIONS[0];
    if (next === mentionEmailImmediately) {
      return;
    }
    await handleDigestToggle(
      setMentionEmailImmediately,
      mentionEmailImmediately,
      PreferencesService.setMentionEmailEnabled,
      next,
    );
  };

  const handleDigestFrequencySelect = async (label: string) => {
    const next =
      digestFrequencyByLabel.get(label) ??
      parseDigestFrequency(label, { allowMinute: allowMinuteFrequency });
    if (!next || next === digestFrequency) {
      return;
    }
    const previousValue = digestFrequency;
    try {
      setDigestFrequency(next);
      await PreferencesService.setDigestFrequency(next);
    } catch (error) {
      console.error('Error saving digest frequency preference:', error);
      Alert.alert('Error', 'Failed to save preference. Please try again.', [
        { text: 'OK' },
      ]);
      setDigestFrequency(previousValue);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Header
          currentPath={currentPath || '/preferences'}
          navigate={navigate}
        />
        <SubHeader title="Application Preferences" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FF6B35" />
          <Text style={styles.loadingText}>Loading preferences...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Header currentPath={currentPath || '/preferences'} navigate={navigate} />
      <SubHeader title="Application Preferences" />

      <ScrollView
        ref={scrollRef}
        style={styles.content}
        contentContainerStyle={[
          styles.contentContainer,
          keyboardHeight > 0 && {
            paddingBottom: CONTENT_PADDING + keyboardHeight,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        testID="preferences-scroll-view"
      >
        <View ref={scrollContentRef} style={styles.contentWrapper}>
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Sliders size={20} color="#666" />
              <Text style={styles.sectionTitle}>File Management</Text>
            </View>

            <View style={styles.sectionContent}>
              <ToggleSwitch
                value={retainAudioOcrFiles}
                onValueChange={handleToggleChange}
                label="Retain Audio / OCR Files"
                description="When enabled, audio and OCR source files will be stored as attachments when creating requests. Files are stored even in failure scenarios."
              />
            </View>
          </View>

          {/* Email Notifications Section - shown for all users (#1399).
              All users see the global toggle; supervisors also see their
              region toggles below it based on existing role rules. */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Bell size={20} color="#666" />
              <Text style={styles.sectionTitle}>Email Notifications</Text>
            </View>

            <View style={styles.sectionContent}>
              <ToggleSwitch
                testID="toggle-email-notifications"
                value={emailNotifications}
                onValueChange={handleEmailNotificationsToggle}
                label="Request Email Notifications"
                description="Get emails about your requests and team updates. Turn off to stop Site Scope request and team notification emails to you."
              />

              {isSupervisorNAM && (
                <ToggleSwitch
                  value={supervisorEmailNAM}
                  onValueChange={value =>
                    handleSupervisorToggleChange('NAM', value)
                  }
                  disabled={!emailNotifications}
                  label="Supervisor NAM Email Notifications"
                  description="Receive email notifications for North America region requests."
                />
              )}

              {isSupervisorEMEA && (
                <ToggleSwitch
                  value={supervisorEmailEMEA}
                  onValueChange={value =>
                    handleSupervisorToggleChange('EMEA', value)
                  }
                  disabled={!emailNotifications}
                  label="Supervisor EMEA Email Notifications"
                  description="Receive email notifications for Europe/Middle East/Africa region requests."
                />
              )}

              {isSupervisorAPAC && (
                <ToggleSwitch
                  value={supervisorEmailAPAC}
                  onValueChange={value =>
                    handleSupervisorToggleChange('APAC', value)
                  }
                  disabled={!emailNotifications}
                  label="Supervisor APAC Email Notifications"
                  description="Receive email notifications for Asia-Pacific region requests."
                />
              )}
            </View>
          </View>

          {/* Change notification emails (#1507). Two switches, one frequency
              for both, the Mention emails choice (#1599 Story 2.4, only while
              the deployment has mentions), and the oversight switch for
              oversight roles only. The #1399 cascade applies: every row is
              disabled (stored values shown) while the global switch is off,
              and turning the global switch back on never flips one of these
              by itself. */}
          <View style={styles.section} testID="section-digest-emails">
            <View style={styles.sectionHeader}>
              <Mail size={20} color="#666" />
              <Text style={styles.sectionTitle}>
                Change Notification Emails
              </Text>
            </View>

            <View style={styles.sectionContent}>
              <Text style={styles.sectionNote}>
                These settings control the change notification emails only.
                Unread rows and discussion dots in the app are unaffected.
              </Text>

              <ToggleSwitch
                testID="toggle-digest-request-changes"
                value={digestRequestChanges}
                onValueChange={value =>
                  handleDigestToggle(
                    setDigestRequestChanges,
                    digestRequestChanges,
                    PreferencesService.setDigestRequestChangesEnabled,
                    value,
                  )
                }
                disabled={!emailNotifications}
                label="Request Changes"
                description="Email me about changes to requests I can see."
              />

              <ToggleSwitch
                testID="toggle-digest-correspondence"
                value={digestCorrespondence}
                onValueChange={value =>
                  handleDigestToggle(
                    setDigestCorrespondence,
                    digestCorrespondence,
                    PreferencesService.setDigestCorrespondenceEnabled,
                    value,
                  )
                }
                disabled={!emailNotifications}
                label="Discussion Messages"
                description="Email me about new discussion messages on requests I can see."
              />

              <View style={styles.toggleContainer}>
                <View style={styles.toggleLabelContainer}>
                  <Text style={styles.toggleLabel}>Frequency</Text>
                  <Text style={styles.toggleDescription}>
                    {showMentionEmailChoice
                      ? DIGEST_FREQUENCY_HINT
                      : DIGEST_FREQUENCY_HINT_WITHOUT_MENTIONS}
                  </Text>
                </View>
                <TouchableOpacity
                  testID="digest-frequency-picker"
                  accessibilityRole="button"
                  accessibilityLabel={`Frequency: ${digestFrequencyLabel(
                    digestFrequency,
                  )}`}
                  accessibilityState={{ disabled: !emailNotifications }}
                  style={[
                    styles.frequencyTrigger,
                    !emailNotifications && styles.toggleDisabled,
                  ]}
                  onPress={() =>
                    emailNotifications && setFrequencyPickerOpen(true)
                  }
                  disabled={!emailNotifications}
                  activeOpacity={0.7}
                >
                  <Text style={styles.frequencyTriggerText}>
                    {digestFrequencyLabel(digestFrequency)}
                  </Text>
                  <ChevronDown size={16} color="#666" />
                </TouchableOpacity>
              </View>

              {showMentionEmailChoice && (
                <View
                  style={styles.choiceRow}
                  testID="mention-email-choice-row"
                >
                  <FormFieldLabelWithInfo
                    onInfo={() => setMentionEmailInfoOpen(true)}
                    infoA11yHint="Open help for mention notifications"
                    a11yDescription={[
                      MENTION_EMAIL_INFO_TITLE,
                      ...MENTION_EMAIL_INFO_BULLETS,
                    ].join(' ')}
                    inlineWithControl
                    showInfoIcon
                    labelTextStyle={styles.choiceLabel}
                  >
                    {MENTION_EMAIL_INFO_TITLE}
                  </FormFieldLabelWithInfo>
                  <Text style={styles.toggleDescription}>
                    How notifications reach you when someone @mentions you.
                  </Text>
                  {/* `editable` rides with `disabled`: disabled alone renders
                      the stored value as plain text, and the cascade must
                      show the radios, dimmed, with the value still selected. */}
                  <FormTwoOptionChoice
                    testID="mention-email-choice"
                    label={MENTION_EMAIL_INFO_TITLE}
                    hideLabel
                    value={mentionEmailOption(mentionEmailImmediately)}
                    options={MENTION_EMAIL_OPTIONS}
                    onSelect={handleMentionEmailSelect}
                    disabled={!emailNotifications}
                    editable={emailNotifications}
                    containerStyle={styles.choiceRadios}
                  />
                </View>
              )}

              {isOversightRole && (
                <ToggleSwitch
                  testID="toggle-digest-include-oversight"
                  value={digestIncludeOversight}
                  onValueChange={value =>
                    handleDigestToggle(
                      setDigestIncludeOversight,
                      digestIncludeOversight,
                      PreferencesService.setDigestIncludeOversight,
                      value,
                    )
                  }
                  disabled={!emailNotifications}
                  label="Include Requests I Oversee"
                  description="Also email me about requests I can open because of my role, even when I'm not on them. Off means only requests I'm on."
                />
              )}
            </View>
          </View>
          {showApprovalsCard && (
            <View style={styles.section} testID="preferences-approvals-card">
              <View style={styles.sectionHeader}>
                <Users size={20} color="#666" />
                <Text style={styles.sectionTitle}>Approvals</Text>
              </View>

              <View style={styles.sectionContent}>
                {isPublicDomainApprovals ? (
                  <Text
                    testID="approvals-public-domain-note"
                    style={styles.toggleDescription}
                  >
                    {APPROVALS_PUBLIC_DOMAIN_NOTE}
                  </Text>
                ) : approvalLoading ? (
                  <Text style={styles.toggleDescription}>
                    Loading Approvals...
                  </Text>
                ) : (
                  <>
                    {approvalError && (
                      <Text style={styles.toggleDescription}>
                        Unable to load Approvals. Member changes are disabled
                        until reload succeeds.
                      </Text>
                    )}
                    <ToggleSwitch
                      testID="toggle-approvals-group"
                      value={approvalsWorkflowOn}
                      onValueChange={value => {
                        void handleApprovalsEnabledToggle(value);
                      }}
                      disabled={!approvalsToggleEnabled}
                      label="Approvals Group"
                      description={
                        approvalsSetupIncomplete
                          ? 'Add at least one group member to complete setup.'
                          : 'When enabled, partner users submit requests to this group first. A group member approves or rejects before the request is sent to Vertiv.'
                      }
                    />

                    {approvalsWorkflowOn ? (
                      <View
                        testID="approvals-members-panel"
                        style={styles.approvalsMembersPanel}
                      >
                        <View
                          ref={approvalsCandidateSearchBlockRef}
                          style={styles.approvalsCandidateSearch}
                        >
                          <Text style={styles.approvalsFieldLabel}>
                            Add group members
                          </Text>
                          <AdminTableHeaderSearch
                            inputRef={approvalCandidateSearchRef}
                            value={candidateSearch}
                            onChangeText={handleCandidateSearchChange}
                            onFocus={() => setApprovalsSearchFocused(true)}
                            onBlur={() => setApprovalsSearchFocused(false)}
                            focused={approvalsSearchFocused}
                            placeholder="Search by name or email..."
                            accessibilityLabel="Search users to add to Approvals Group"
                            searchBoxTestID="approvals-candidate-search-box"
                            searchInputTestID="approvals-candidate-search"
                            isSearching={candidatesLoading}
                            editable={membersEditable && !approvalsAddBusy}
                            boxStyle={styles.approvalsCandidateSearchBox}
                            inputAccessoryViewID={ACCESSORY_VIEW_ID}
                          />
                          <Text style={styles.approvalsSearchHelper}>
                            Search users from {viewerDomain}.
                          </Text>
                        </View>

                        {isCandidateSearchActive ? (
                          <AdminTable
                            testID="approvals-candidates-grid"
                            accessibilityLabel="Users available to add"
                          >
                            {!candidatesLoading && !candidatesError ? (
                              <View style={styles.approvalsCandidateSummary}>
                                <Text
                                  style={styles.approvalsCandidateSummaryText}
                                >
                                  {visibleCandidates.length}{' '}
                                  {visibleCandidates.length === 1
                                    ? 'matching user'
                                    : 'matching users'}{' '}
                                  from {viewerDomain}
                                </Text>
                              </View>
                            ) : null}

                            {candidatesError ? (
                              <AdminTableInlineMessageRow message="Unable to search users. Try again." />
                            ) : null}

                            {visibleCandidates.map(candidate => {
                              const candidateId = Number(candidate.userId);
                              const selected =
                                selectedCandidateIds.has(candidateId);
                              return (
                                <TouchableOpacity
                                  key={`candidate-${candidateId}`}
                                  testID={`approvals-candidate-${candidateId}`}
                                  accessibilityRole="checkbox"
                                  accessibilityLabel={`${candidate.name}, ${candidate.email}`}
                                  accessibilityState={{ checked: selected }}
                                  onPress={() =>
                                    handleCandidateSelection(candidateId)
                                  }
                                  disabled={
                                    !membersEditable || approvalsAddBusy
                                  }
                                  style={[
                                    adminTableStyles.row,
                                    adminTableMemberGridStyles.gridRow,
                                    styles.approvalsCandidateRow,
                                    selected &&
                                      styles.approvalsCandidateRowSelected,
                                  ]}
                                >
                                  <View
                                    style={[
                                      styles.approvalsCandidateCheckbox,
                                      selected &&
                                        styles.approvalsCandidateCheckboxSelected,
                                    ]}
                                  >
                                    {selected ? (
                                      <Text
                                        style={
                                          styles.approvalsCandidateCheckmark
                                        }
                                      >
                                        ✓
                                      </Text>
                                    ) : null}
                                  </View>
                                  <Text
                                    style={adminTableStyles.primaryCell}
                                    numberOfLines={1}
                                  >
                                    {candidate.name}
                                  </Text>
                                  <Text
                                    style={[
                                      adminTableStyles.secondaryCell,
                                      adminTableMemberGridStyles.emailCellCentered,
                                    ]}
                                    numberOfLines={1}
                                  >
                                    {candidate.email}
                                  </Text>
                                </TouchableOpacity>
                              );
                            })}

                            {visibleCandidates.length === 0 &&
                            !candidatesLoading &&
                            !candidatesError ? (
                              <AdminTableInlineMessageRow message="No users match your search." />
                            ) : null}

                            <View style={styles.approvalsCandidateFooter}>
                              <Text style={styles.approvalsSelectedCount}>
                                {selectedCandidateIds.size} selected
                              </Text>
                              <TouchableOpacity
                                testID="approvals-add-members"
                                accessibilityRole="button"
                                accessibilityLabel={
                                  selectedCandidateIds.size === 1
                                    ? 'Add 1 selected user'
                                    : `Add ${selectedCandidateIds.size} selected users`
                                }
                                onPress={() => {
                                  void handleAddApprovalMembers();
                                }}
                                disabled={
                                  selectedCandidateIds.size === 0 ||
                                  !membersEditable ||
                                  approvalsAddBusy
                                }
                                style={[
                                  styles.approvalsAddButton,
                                  (selectedCandidateIds.size === 0 ||
                                    !membersEditable ||
                                    approvalsAddBusy) &&
                                    styles.approvalsAddButtonDisabled,
                                ]}
                              >
                                <Text style={styles.approvalsAddButtonText}>
                                  Add
                                </Text>
                              </TouchableOpacity>
                            </View>
                          </AdminTable>
                        ) : null}

                        <View style={styles.approvalsMemberTable}>
                          <AdminTable
                            testID="approvals-members-grid"
                            accessibilityLabel="Approvals group members"
                          >
                            <AdminTableNameEmailHeader
                              trailingLabel="Email"
                              trailingLabelAlign="center"
                              showActionsColumn
                            >
                              <Text style={adminTableStyles.headerText}>
                                Name
                              </Text>
                            </AdminTableNameEmailHeader>

                            {(approvalGroup?.members ?? []).length === 0 ? (
                              <AdminTableInlineMessageRow message="No members yet. Search by name or email to add a user." />
                            ) : null}

                            {(approvalGroup?.members ?? []).map(member => (
                              <AdminTableNameEmailRow
                                key={member.userId}
                                name={member.name}
                                email={member.email}
                                emailAlign="center"
                                testID={`approvals-member-${member.userId}`}
                                accessibilityLabel={`${member.name}, ${member.email}`}
                                trailing={
                                  <TouchableOpacity
                                    testID={`approvals-remove-member-${member.userId}`}
                                    onPressIn={() =>
                                      setTrashPressedUserId(member.userId)
                                    }
                                    onPressOut={() =>
                                      setTrashPressedUserId(null)
                                    }
                                    onPress={() => {
                                      if (
                                        !membersEditable ||
                                        approvalsAddBusy
                                      ) {
                                        return;
                                      }
                                      void handleRemoveApprovalMember(
                                        member.userId,
                                      );
                                    }}
                                    disabled={
                                      !membersEditable || approvalsAddBusy
                                    }
                                    accessibilityLabel={`Remove ${member.name}`}
                                    style={
                                      adminTableMemberGridStyles.rowIconAction
                                    }
                                  >
                                    <Trash2
                                      size={20}
                                      color={
                                        trashPressedUserId === member.userId
                                          ? colors.error
                                          : '#666'
                                      }
                                    />
                                  </TouchableOpacity>
                                }
                              />
                            ))}
                          </AdminTable>
                        </View>
                      </View>
                    ) : null}
                  </>
                )}
              </View>
            </View>
          )}

          <View style={styles.section} testID="preferences-timezone-card">
            <View style={styles.sectionHeader}>
              <Clock size={20} color="#666" />
              <Text style={styles.sectionTitle}>Time zone</Text>
            </View>
            <View style={styles.sectionContent}>
              {zoneLoaded ? (
                <>
                  {zoneLoadFailed ? (
                    <Text
                      style={styles.timeZoneLoadError}
                      testID="preferences-timezone-load-error"
                    >
                      Couldn't load your saved time zone. Reload Preferences
                      before changing it.
                    </Text>
                  ) : null}
                  <TimeZoneFields
                    timeZone={timeZone}
                    syncWithDevice={syncWithDevice}
                    fieldDisabled={zoneLoadFailed || syncWithDevice}
                    switchDisabled={zoneLoadFailed}
                    onTimeZoneChange={handleTimeZoneChange}
                    onSyncChange={handleSyncChange}
                  />
                </>
              ) : (
                <ActivityIndicator
                  testID="preferences-timezone-loading"
                  color="#FF6B35"
                />
              )}
            </View>
          </View>
        </View>
      </ScrollView>
      <DropdownModal
        visible={frequencyPickerOpen}
        onClose={() => setFrequencyPickerOpen(false)}
        title="Frequency"
        options={digestFrequencyOptions}
        selectedValue={digestFrequencyLabel(digestFrequency)}
        onSelect={handleDigestFrequencySelect}
      />
      <FormInfoHelpModal
        visible={mentionEmailInfoOpen}
        onClose={() => setMentionEmailInfoOpen(false)}
        title={MENTION_EMAIL_INFO_TITLE}
        bullets={MENTION_EMAIL_INFO_BULLETS}
      />
      <KeyboardDismissToolbar />
      {confirmationDialog}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: CONTENT_PADDING,
    alignItems: isWeb ? 'center' : ('stretch' as const),
    flexGrow: 1,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: isWeb ? 1200 : undefined,
    alignSelf: isWeb ? 'center' : ('stretch' as const),
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    marginBottom: 20,
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    gap: 12,
    backgroundColor: '#f8f9fa',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  timeZoneLoadError: {
    fontSize: 13,
    color: '#dc3545',
    marginBottom: 8,
  },
  sectionContent: {
    padding: 16,
  },
  sectionNote: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  frequencyTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 30,
    paddingHorizontal: 12,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  frequencyTriggerText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  // The Mention emails row (#1599 Story 2.4): the section's row rhythm, but a
  // column, so the radios sit under the label and wrap at 320pt.
  choiceRow: {
    paddingVertical: 12,
  },
  choiceRadios: {
    marginTop: 8,
  },
  // The row's label at its siblings' size (owner quick-dev 2026-10-27): the
  // shared form label is 14pt, every other row label on this card is
  // `toggleLabel`. Same values, without `toggleLabel`'s margin, which the
  // label component's own spacing already provides. The line height replaces
  // the form label's 16, which would clip a 16pt glyph.
  choiceLabel: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '500',
    color: colors.textPrimary,
  },
  toggleLabelContainer: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  toggleDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  toggleTrack: {
    width: 50,
    height: 30,
    borderRadius: 15,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  toggleTrackInner: {
    width: '100%',
    height: '100%',
    borderRadius: 15,
    justifyContent: 'center',
    padding: 2,
  },
  toggleDisabled: {
    opacity: 0.5,
  },
  approvalsMembersPanel: {
    marginTop: 12,
  },
  approvalsCandidateSearch: {
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  approvalsFieldLabel: {
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: '500',
  },
  approvalsCandidateSearchBox: {
    width: '100%',
    maxWidth: '100%',
  },
  approvalsSearchHelper: {
    color: colors.textSecondary,
    fontSize: 12,
  },
  approvalsCandidateSummary: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: colors.backgroundMuted,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  approvalsCandidateSummaryText: {
    color: colors.textSecondary,
    fontSize: 12,
  },
  approvalsCandidateRow: {
    gap: spacing.sm,
  },
  approvalsCandidateRowSelected: {
    backgroundColor: colors.surfaceAccentMuted,
  },
  approvalsCandidateCheckbox: {
    width: spacing.xl,
    height: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.backgroundPrimary,
  },
  approvalsCandidateCheckboxSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  approvalsCandidateCheckmark: {
    color: colors.textLight,
    fontSize: 13,
    fontWeight: '700',
  },
  approvalsCandidateFooter: {
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: colors.backgroundMuted,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  approvalsSelectedCount: {
    color: colors.textSecondary,
    fontSize: 12,
  },
  approvalsAddButton: {
    minHeight: 44,
    minWidth: 72,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.primary,
  },
  approvalsAddButtonDisabled: {
    opacity: 0.5,
  },
  approvalsAddButtonText: {
    color: colors.textLight,
    fontSize: 14,
    fontWeight: '600',
  },
  approvalsMemberTable: {
    marginTop: spacing.md,
  },
  toggleThumb: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
});
